import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { useBreakpoints } from 'hooks';
import { getColorType, setColorType } from 'PDF/helpers/documentStyle';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getSetBusinessPlan } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { UPDATE_HANDLER_ARGUMENTS } from 'pages/BusinessToolPage/components/PreviewModeComponent/hooks/constants';
import { IColorOptionUpdateParams } from 'pages/BusinessToolPage/components/PreviewModeComponent/hooks/types';
import { useDebounceUpdateMetadataHandler } from 'pages/BusinessToolPage/components/PreviewModeComponent/hooks/useDebounceUpdateMetadataHandler';

import { ReactComponent as CheckCircleSmall } from 'assets/icons/CheckCircleSmall.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { OPTIONS } from './constants';
import { IColorOptionTypes } from './types';
import { TooltipComponent } from '../TooltipComponent';

import styles from './ColorSelector.module.scss';

export const ColorSelector: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);

  const { isAuthenticated } = useAuthenticated();

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const selectedColor = businessPlan.metadata.documentColorType || getColorType() || 'green';

  const handleColorSelect = (color: IColorOptionTypes, index: number) => {
    if (selectedColor === color) return;
    if (index && !isStandardOrLightTariff) {
      return;
    }
    setColorType(color);
    setBusinessPlan({ ...businessPlan, metadata: { ...businessPlan.metadata, documentColorType: color } });
    setColorType(color);

    if (isAuthenticated) {
      debouncedUpdateHandler(color, businessPlan, selectedColor);
    }
  };

  const debouncedUpdateHandler = useDebounceUpdateMetadataHandler<IColorOptionUpdateParams>({
    metaDataKey: UPDATE_HANDLER_ARGUMENTS.COLOR_TYPE.METADATA_KEY,
    errorMessage: UPDATE_HANDLER_ARGUMENTS.COLOR_TYPE.ERROR_MESSAGE,
  });

  useEffect(() => {
    setColorType(selectedColor);
  }, []);

  return (
    <Box className={styles.colorSelectContainer}>
      <Typography variant="h5" className={styles.selectColorText}>
        {t('selectColor')}
      </Typography>
      <Box className={styles.colorContainer}>
        {OPTIONS.map(({ color, icon }, index) => {
          const isSelected = selectedColor === color;
          const disabledOption = !!index && !isStandardOrLightTariff;
          return (
            <TooltipComponent
              key={color}
              title={t('upgradePlanToGetFeature')}
              isTooltipHidden={!index || isStandardOrLightTariff}
              trigger={isMobile ? 'click' : 'hover'}
              leaveDelay={isMobile ? 6500 : 200}
            >
              <Box
                className={clsx(
                  styles.colorSelectStyle,
                  { [styles.outlineStyle]: isSelected },
                  { [styles.disabledStyle]: disabledOption },
                )}
                onClick={() => handleColorSelect(color, index)}
              >
                {icon}
                {isSelected && (
                  <Box className={styles.checkCircle}>
                    <CheckCircleSmall />
                  </Box>
                )}
              </Box>
            </TooltipComponent>
          );
        })}
      </Box>
    </Box>
  );
};
